import { useEffect, useState } from "react";
import { Link, useRouter } from "../../router";
import { ClientTypeIcon } from "../../components/ClientTypeIcon";
import { advisoryApi, isUnavailable, type AdvisorySession } from "./advisoryApi";
import { relative, statusLabel, statusPill } from "./format";
import { PercentBar, PoweredBy } from "./ui";
import "./advisory.css";

// Filter tabs over the server's status buckets (§4.1).
const FILTERS: { key: string; label: string }[] = [
  { key: "", label: "All" },
  { key: "in_progress", label: "In progress" },
  { key: "submitted", label: "Submitted" },
  { key: "signing", label: "Signing" },
  { key: "completed", label: "Completed" },
];

function goalCount(s: AdvisorySession): number | null {
  if (!s.advice_json) return null;
  try {
    const a = JSON.parse(s.advice_json) as { goals?: unknown[] };
    return Array.isArray(a.goals) ? a.goals.length : null;
  } catch {
    return null;
  }
}

/** The advisor's desk: every advice session, filterable by lifecycle bucket. */
export function AdvisoryHome() {
  const { navigate } = useRouter();
  const [filter, setFilter] = useState("");
  const [sessions, setSessions] = useState<AdvisorySession[] | null>(null);
  const [err, setErr] = useState<{ msg: string; unavailable: boolean } | null>(null);

  useEffect(() => {
    let stale = false;
    setSessions(null);
    setErr(null);
    advisoryApi
      .sessions(filter || undefined)
      .then((d) => !stale && setSessions(d.sessions))
      .catch((e) => !stale && setErr({ msg: (e as Error).message, unavailable: isUnavailable(e) }));
    return () => {
      stale = true;
    };
  }, [filter]);

  return (
    <div className="page adv-page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Advisory</span>
          <h1>Advice desk</h1>
        </div>
        <div className="page-head-actions">
          <Link to="/app/advisory/new" className="btn btn-primary">
            New advice
          </Link>
        </div>
      </header>
      <p className="muted">
        Every investment advice you are giving, from the first meeting notes to the suitability
        report. Open one to continue where you left off.
      </p>

      <div className="tabs" role="tablist">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            type="button"
            role="tab"
            aria-selected={filter === f.key}
            className={filter === f.key ? "tab-link active" : "tab-link"}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {err ? (
        <div className="banner-error">{err.msg}</div>
      ) : sessions === null ? (
        <p className="muted">Loading advice sessions…</p>
      ) : sessions.length === 0 ? (
        <div className="empty">
          <span className="eyebrow">{filter ? "Nothing here" : "No advice yet"}</span>
          <p>
            {filter
              ? "No advice sessions in this state."
              : "Start the first advice for one of your clients."}
          </p>
          {!filter && (
            <Link to="/app/advisory/new" className="btn btn-ghost">
              Start an advice →
            </Link>
          )}
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Flow</th>
              <th className="num">Goals</th>
              <th>Status</th>
              <th>Complete</th>
              <th>Last touched</th>
              <th>Owner</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id} className="row-link" onClick={() => navigate(`/app/advisory/${s.id}`)}>
                <td className="strong">
                  <span className="cell-with-icon">
                    {s.investor_type && <ClientTypeIcon type={s.investor_type} className="type-icon" />}
                    {s.investor_name ?? "—"}
                  </span>
                </td>
                <td>{s.flow_name ?? "—"}</td>
                <td className="num">{goalCount(s) ?? "—"}</td>
                <td>
                  <span className={statusPill(s.status)}>{statusLabel(s.status)}</span>
                </td>
                <td className="adv-cell-percent">
                  <PercentBar value={s.percent} compact />
                </td>
                <td className="muted">{relative(s.updated_at)}</td>
                <td>{s.advisor_username ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {err?.unavailable && (
        <p className="muted">The advice engine is not reachable right now. Try again in a moment.</p>
      )}
      <PoweredBy />
    </div>
  );
}
